import React, { useState, useEffect } from 'react';
import { auth, db, functions } from '../../config/firebase';
import { collection, doc, getDocs, query, where, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { formatMoment } from '../../utils/dates';
import { onAuthStateChanged } from 'firebase/auth';
import { useToast } from '../../components/ui/ToastProvider';
import { H1, H2 } from '../../components/typography';
import Button from '../../components/button';
import { User, Trash2, Shield, Plus, Mail } from 'lucide-react';
import { Dialog } from '@headlessui/react';

export default function Settings() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [currentAssociationId, setCurrentAssociationId] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const toast = useToast();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Hämta användarens BRF-association
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setCurrentAssociationId(userData.associationId || '');
          fetchAdmins(userData.associationId || '');
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Listan hämtades förut på föreningens NAMN som sträng. Hälften av admin-
  // kontona i dev hade ett namn som inte matchade — de syntes aldrig, och
  // "kan inte ta bort" var lika ofta "kan inte se". Id:t ljuger inte.
  const fetchAdmins = async (associationId) => {
    if (!associationId) {
      setAdmins([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('role', '==', 'brfadmin'),
        where('associationId', '==', associationId)
      );
      const querySnapshot = await getDocs(q);
      
      const adminList = [];
      querySnapshot.forEach((doc) => {
        adminList.push({ id: doc.id, ...doc.data() });
      });
      
      setAdmins(adminList);
    } catch (error) {
      toast({ type: 'error', message: 'Kunde inte hämta administratörer' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const { firstName, lastName, email } = form;

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast({ type: 'error', message: 'Alla fält måste fyllas i' });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({ type: 'error', message: 'Ogiltig e-postadress' });
      return false;
    }
    
    return true;
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);
    try {
      // Kontot skapas server-side. Klienten KAN inte göra det: `users` har
      // `allow create: if false` i reglerna. Den gamla vägen försökte ändå och
      // kapade dessutom sessionen — createUserWithEmailAndPassword loggade in
      // webbläsaren som den nya användaren och slängde ut styrelsen mitt i.
      const addAdminFn = httpsCallable(functions, 'addBrfAdmin');
      const { data } = await addAdminFn({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        associationId: currentAssociationId,
      });

      const { uid, ...userDoc } = data || {};
      setAdmins(prev => [...prev, { id: uid, ...userDoc }]);

      toast({
        type: 'success',
        message: 'Administratören är tillagd. Be hen sätta lösenord via "Glömt lösenord?" på inloggningssidan.',
      });
      setModalOpen(false);
      setForm({ firstName: '', lastName: '', email: '' });
    } catch (error) {
      // Funktionen svarar med begripliga meddelanden (upptagen adress,
      // saknad behörighet). Visa dem i stället för en generisk text.
      toast({
        type: 'error',
        message: error?.message || 'Kunde inte lägga till administratör',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAdmin = (admin) => {
    if (admin.id === auth.currentUser?.uid) {
      toast({ type: 'error', message: 'Du kan inte ta bort dig själv' });
      return;
    }
    
    setAdminToDelete(admin);
    setDeleteModalOpen(true);
  };

  const confirmRemoveAdmin = async () => {
    if (!adminToDelete) return;
    
    try {
      // deleteDoc från klienten nekas alltid av rules (users: delete false).
      // Servern gör det, och säger varför om det inte går.
      const removeAdminFn = httpsCallable(functions, 'removeBrfAdmin');
      const { data } = await removeAdminFn({ adminId: adminToDelete.id });

      setAdmins(prev => prev.filter(admin => admin.id !== adminToDelete.id));

      toast({
        type: 'success',
        message: data?.demotedOnly
          ? 'Styrelsebehörigheten är borttagen. Personens medlemskonto finns kvar.'
          : 'Administratören är borttagen.',
      });
    } catch (error) {
      toast({ type: 'error', message: error?.message || 'Kunde inte ta bort administratör' });
    } finally {
      setDeleteModalOpen(false);
      setAdminToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setForm({ firstName: '', lastName: '', email: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <H1 className="font-bold text-3xl font-['Poppins']" sx={{ marginBottom: '0.5rem' }}>Inställningar</H1>
          <p className="text-zinc-600">Hantera administratörer för din bostadsrättsförening</p>
        </div>
        <Button
          onClick={() => setModalOpen(true)}
          className="bg-[#62B8E9] hover:bg-[#5ba3d4] text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Lägg till administratör
        </Button>
      </div>

      {/* Administratörer lista */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <H2 className="font-semibold text-xl flex items-center gap-2 font-['Poppins']">
            <Shield className="w-6 h-6 text-[#62B8E9]" />
            Administratörer ({admins.length})
          </H2>
        </div>
        
        {admins.length === 0 ? (
          <div className="p-8 text-center">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Inga administratörer hittades</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {admins.map((admin) => (
              <div key={admin.id} className="p-6 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#62B8E9] rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-zinc-900">
                      {admin.firstName} {admin.lastName}
                      {admin.id === auth.currentUser?.uid && (
                        <span className="ml-2 text-sm text-[#62B8E9] font-medium">(Du)</span>
                      )}
                    </h4>
                    <p className="text-gray-600 flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {admin.email}
                    </p>
                    {admin.createdAt && (
                      <p className="text-sm text-gray-500">
                        Skapad: {formatMoment(admin.createdAt) || 'okänt datum'}
                      </p>
                    )}
                  </div>
                </div>
                
                {admin.id !== auth.currentUser?.uid && (
                  <Button
                    onClick={() => handleRemoveAdmin(admin)}
                    className="bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Ta bort
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal för att lägga till administratör */}
      <Dialog open={modalOpen} onClose={handleCloseModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
            <Dialog.Title className="text-2xl font-bold text-zinc-900 mb-6 flex items-center gap-2">
              <Plus className="w-6 h-6 text-[#62B8E9]" />
              Lägg till administratör
            </Dialog.Title>
            
            <form onSubmit={handleAddAdmin} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Förnamn
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#62B8E9] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Efternamn
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#62B8E9] focus:border-transparent"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-postadress
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#62B8E9] focus:border-transparent"
                    required
                  />
                </div>
              </div>
              
              <p className="rounded-xl bg-blue-50 px-4 py-3 text-sm text-gray-700">
                Administratören får inget lösenord av dig. Be hen välja
                <strong> Är du styrelsemedlem?</strong> på inloggningssidan och
                sedan <strong>Glömt lösenord?</strong> för att sätta ett eget.
              </p>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors"
                >
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#62B8E9] hover:bg-[#5ba3d4] text-white py-3 px-4 rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'Sparar...' : 'Lägg till'}
                </Button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Confirmation dialog för att ta bort administratör */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
            <Dialog.Title className="text-2xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-red-600" />
              Ta bort administratör
            </Dialog.Title>
            
            <p className="text-gray-600 mb-6">
              Är du säker på att du vill ta bort administratören{' '}
              <span className="font-semibold text-zinc-900">
                {adminToDelete?.firstName} {adminToDelete?.lastName}
              </span>{' '}
              ({adminToDelete?.email})?
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setDeleteModalOpen(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors"
              >
                Avbryt
              </Button>
              <Button
                onClick={confirmRemoveAdmin}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-xl font-medium transition-colors"
              >
                Ta bort
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
} 